<script setup>
// The calendar card of TapDatePicker (the .tdp kit: quick chips, month and year
// selects, ‹ › month nav and the month grid; 36px targets, today ringed in
// brand, selected brand-filled, out-of-range days disabled). TapDatePicker
// shows it in its popover under the typed field. X91 (Grant 2026-10-21: the
// booking strip's range button "can just show the date picker immediately"):
// the booking week strip shows it alone in a popover on its range button, so a
// pick is one tap, with no field and no dialog. It emits `pick` with the picked
// day (a luxon DateTime at the start of the day); what that becomes is the
// caller's business (TapDatePicker runs it through its typing path).
import {computed, ref, watch} from 'vue';
import {DateTime} from 'luxon';
import {
  canStep, chipsToOffer, dateOfBirthPickerStart, dayOutOfRange, monthToOpenOn, monthToShow, monthsToOffer,
  opensOnYears, yearsToOffer,
} from '~/composables/useDatePickerRange';

const props = defineProps({
  /** The selected day as an ISO instant (null for none). */
  selectedIso: {type: String, default: null},
  /** ISO dates bounding the window, TapDatePicker's rules: min undefined = today, '' = no bound; max undefined = none. */
  min: {type: String, default: undefined},
  max: {type: String, default: undefined},
  birthDate: {type: Boolean, default: false},
  quick: {type: Array, default: () => []},
  /** The popover holding the card is open: the grid snaps back to the selection each time it opens. */
  open: {type: Boolean, default: true},
});
const emit = defineEmits(['pick']);

// Luxon drives the grid (month arithmetic, week starts).
const today = () => DateTime.now().startOf('day');

const asDay = (isoValue) => {
  if (!isoValue) return null;
  const d = DateTime.fromJSDate(new Date(isoValue));
  return d.isValid ? d.startOf('day') : null;
};

const selected = computed(() => asDay(props.selectedIso));
const minDay = computed(() => (props.min === undefined ? today() : asDay(props.min)));
const maxDay = computed(() => (props.max === undefined ? null : asDay(props.max)));

// Everything about WHICH days, months and years may be offered lives in
// useDatePickerRange, so it can be tested without a DOM. This component only
// wires it to the markup.
const openAt = () => (props.birthDate
  ? dateOfBirthPickerStart({selected: selected.value, min: minDay.value, max: maxDay.value}).startOf('month')
  : monthToOpenOn({selected: selected.value, min: minDay.value, max: maxDay.value}));

// The year list a date of birth opens on. Picking a year drops to that year's
// month grid; the answer is still a day the member picks.
const pickingYear = ref(false);
const pickYear = (year) => {
  showMonth(year, viewMonth.value.month);
  pickingYear.value = false;
};

// Month shown in the grid — snaps to the selection when the popover opens or
// a full date is typed.
const viewMonth = ref(openAt());
watch(() => props.open, (open) => {
  if (open) {
    viewMonth.value = openAt();
    pickingYear.value = opensOnYears({birthDate: props.birthDate, selected: selected.value});
  }
}, {immediate: true});
watch(selected, (d) => {
  if (d) viewMonth.value = d.startOf('month');
});

const dowLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const yearOptions = computed(() => yearsToOffer({
  min: minDay.value,
  max: maxDay.value,
  viewYear: viewMonth.value.year,
  selectedYear: selected.value?.year,
}));
const monthOptions = computed(() => monthsToOffer(viewMonth.value.year, minDay.value, maxDay.value));

const showMonth = (year, month) => {
  const target = monthToShow(Number(year), Number(month), minDay.value, maxDay.value);
  if (target) viewMonth.value = DateTime.fromObject({year: target.year, month: target.month, day: 1});
};

const viewYear = computed({
  get: () => viewMonth.value.year,
  set: (year) => showMonth(year, viewMonth.value.month),
});
const viewMonthNumber = computed({
  get: () => viewMonth.value.month,
  set: (month) => showMonth(viewMonth.value.year, month),
});

const step = (months) => {
  const target = viewMonth.value.plus({months});
  showMonth(target.year, target.month);
};
const canStepBack = computed(() => canStep(viewMonth.value, -1, minDay.value, maxDay.value));
const canStepOn = computed(() => canStep(viewMonth.value, 1, minDay.value, maxDay.value));

const grid = computed(() => {
  const days = [];
  const start = viewMonth.value.startOf('week'); // Monday
  const end = viewMonth.value.endOf('month').endOf('week');
  for (let d = start; d <= end; d = d.plus({days: 1})) {
    days.push({
      key: d.toISODate(),
      date: d,
      label: d.day,
      mut: d.month !== viewMonth.value.month,
      today: !props.birthDate && d.hasSame(today(), 'day'),
      sel: selected.value != null && d.hasSame(selected.value, 'day'),
      // One flag for both edges: the grid greys an unpickable day the same way
      // whichever side of the window it falls outside.
      out: dayOutOfRange(d, minDay.value, maxDay.value),
    });
  }
  return days;
});

const commitDay = (day) => emit('pick', day);

const pick = (cell) => {
  if (cell.out) return;
  commitDay(cell.date);
};

const quickChips = computed(() => chipsToOffer(props.quick, minDay.value, maxDay.value));
const pickQuick = (chip) => commitDay(today().plus({days: chip.days}));
</script>

<template>
  <div class="tdp">
    <div v-if="quickChips.length" class="tdp-quick">
      <button
          v-for="chip in quickChips"
          :key="chip.label"
          type="button"
          @click="pickQuick(chip)"
      >{{ chip.label }}</button>
    </div>
    <div v-if="pickingYear" class="tdp-years" role="listbox" aria-label="Year">
      <div class="tdp-years-title">Choose a year</div>
      <div class="tdp-years-grid">
        <button
            v-for="y in yearOptions"
            :key="y"
            type="button"
            class="tdp-year"
            :class="{'tdp-year--at': y === viewMonth.year}"
            :ref="(el) => { if (el && y === viewMonth.year) el.scrollIntoView?.({block: 'center'}); }"
            @click="pickYear(y)"
        >{{ y }}</button>
      </div>
    </div>
    <template v-else>
    <div class="tdp-head">
      <button
          type="button"
          class="tdp-step"
          aria-label="Previous month"
          :disabled="!canStepBack"
          @click="step(-1)"
      >
        <v-icon size="20">mdi-chevron-left</v-icon>
      </button>
      <!-- Native selects on purpose: they teleport nothing, need no second
           overlay inside this one, and open scrolled to the current value
           on every platform. -->
      <div class="tdp-jump">
        <select v-model="viewMonthNumber" aria-label="Month">
          <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>
        <select v-model="viewYear" aria-label="Year">
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
      </div>
      <button
          type="button"
          class="tdp-step"
          aria-label="Next month"
          :disabled="!canStepOn"
          @click="step(1)"
      >
        <v-icon size="20">mdi-chevron-right</v-icon>
      </button>
    </div>
    <div class="tdp-grid">
      <span v-for="(d, i) in dowLabels" :key="`dow-${i}`" class="tdp-dow">{{ d }}</span>
      <span
          v-for="cell in grid"
          :key="cell.key"
          class="tdp-day"
          :class="{
            'tdp-day--mut': cell.mut,
            'tdp-day--today': cell.today,
            'tdp-day--sel': cell.sel,
            'tdp-day--out': cell.out,
          }"
          @click="pick(cell)"
      >{{ cell.label }}</span>
    </div>
    </template>
  </div>
</template>

<style lang="scss">
/* Unscoped: the menu content teleports to body, so scoped styles can't reach
   it. Kill Vuetify's default menu surface — the .tdp card is the whole UI. For
   every v-menu holding this card (TapDatePicker's, the booking strip's). */
.tdp-menu.v-overlay__content {
  min-width: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 16px;
}
</style>

<style scoped lang="scss">
.tdp {
  width: 292px;
  background: rgb(var(--v-theme-surface));
  border: 1.5px solid rgb(var(--v-theme-borderLight));
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
  padding: 12px;
}

.tdp-years-title {
  font-size: 12px;
  font-weight: 700;
  margin: 2px 4px 8px;
  color: rgb(var(--v-theme-onSurface));
}

.tdp-years-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  max-height: 240px;
  overflow-y: auto;
}

.tdp-year {
  height: 36px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-onSurface));

  &:hover {
    background: rgba(var(--v-theme-primary), 0.08);
  }
}

.tdp-year--at {
  outline: 1.5px solid rgb(var(--v-theme-primary));
}

.tdp-quick {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;

  button {
    flex: 1;
    text-align: center;
    font-size: 11.5px;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    background: none;
    border: 1.5px solid rgb(var(--v-theme-borderLight));
    border-radius: 999px;
    padding: 6px 0;
    cursor: pointer;

    &:hover {
      border-color: rgb(var(--v-theme-textSubtle));
    }
  }
}

.tdp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 2px 2px 8px;
}

.tdp-jump {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  select {
    font-size: 13px;
    font-weight: 700;
    font-family: inherit;
    color: rgb(var(--v-theme-on-surface));
    background: none;
    border: 1.5px solid transparent;
    border-radius: 8px;
    padding: 3px 4px;
    cursor: pointer;
    appearance: menulist;

    &:hover {
      border-color: rgb(var(--v-theme-borderLight));
    }

    &:focus-visible {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: 1px;
    }

    option {
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
      background: rgb(var(--v-theme-surface));
    }
  }
}

.tdp-step {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: none;
  border: none;
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgb(var(--v-theme-light));
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
}

.tdp-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.tdp-dow {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-align: center;
  padding: 4px 0 6px;
  text-transform: uppercase;
}

.tdp-day {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-on-surface));
  border-radius: 10px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &--mut {
    color: rgb(var(--v-theme-textSubtle));
    opacity: 0.45;
  }

  &--today {
    box-shadow: inset 0 0 0 1.5px rgb(var(--v-theme-primary));
  }

  &--sel {
    background: rgb(var(--v-theme-primary));
    color: #fff;
    font-weight: 700;

    &:hover {
      background: rgb(var(--v-theme-primary));
    }
  }

  &--out {
    color: rgb(var(--v-theme-textSubtle));
    opacity: 0.35;
    cursor: default;

    &:hover {
      background: none;
    }
  }
}
</style>
